import React, { forwardRef, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import AppStack, { type AppStackItem } from '@atlassian/app-stack';

import { moreAppsControlMessages } from './messages';

// The design shows three icons however many apps are hidden.
export const DEFAULT_MAX_APP_STACK_ICONS = 3;

type TriggerProps = Pick<
	React.ComponentProps<typeof Button>,
	'onClick' | 'aria-haspopup' | 'aria-expanded' | 'aria-controls' | 'isSelected'
>;

export type MoreAppsControlProps = {
	apps: AppStackItem[];
	maxAppStackIcons?: number;
	testId?: string;
} & TriggerProps;

export const MoreAppsControl: React.ForwardRefExoticComponent<
	MoreAppsControlProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, MoreAppsControlProps>(
	(
		{ apps, maxAppStackIcons = DEFAULT_MAX_APP_STACK_ICONS, testId, ...triggerProps },
		ref,
	): React.JSX.Element => {
		const { formatMessage } = useIntl();

		// `iconBefore` takes a component, so an inline arrow would remount the stack every render.
		const AppStackIcon = useMemo(
			() =>
				function MoreAppsStackIcon(): React.JSX.Element {
					return (
						<AppStack
							data={apps}
							label={formatMessage(moreAppsControlMessages.appStackLabel)}
							maxCount={maxAppStackIcons}
							size="xsmall"
							testId={testId ? `${testId}--app-stack` : undefined}
						/>
					);
				},
			[apps, formatMessage, maxAppStackIcons, testId],
		);

		return (
			<Button
				ref={ref}
				{...triggerProps}
				appearance="subtle"
				testId={testId}
				// Must start with the visible "More" so voice control can still target it.
				aria-label={formatMessage(moreAppsControlMessages.moreAppsLabel)}
				iconBefore={AppStackIcon}
				iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
			>
				{formatMessage(moreAppsControlMessages.moreLabel)}
			</Button>
		);
	},
);

MoreAppsControl.displayName = 'MoreAppsControl';
